"use client";

import { ExternalLink, Play } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/shared/ui/buttons/button";

interface TwitchEmbedProps {
  channel: string;
  width?: string | number;
  height?: string | number;
  autoplay?: boolean;
  muted?: boolean;
  quality?: "chunked" | "480p" | "720p" | "1080p" | "160p" | "360p";
  theme?: "light" | "dark";
  layout?: "video-with-chat" | "video";
  time?: string;
  allowFullscreen?: boolean;
  onError?: (error: string) => void;
  onLoad?: () => void;
  onReady?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
}

export function TwitchEmbed({
  channel,
  width = "100%",
  height = "100%",
  autoplay = true,
  muted = false,
  quality = "chunked",
  theme = "dark",
  layout = "video",
  time,
  allowFullscreen = true,
  onError,
  onLoad,
  onReady,
  onPlay,
  onPause,
  onEnded,
}: TwitchEmbedProps) {
  const tUi = useTranslations("Ui");
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Автоматически определяем текущий домен и порт
  const getCurrentHost = () => {
    if (typeof window !== "undefined") {
      return window.location.host;
    }
    return "localhost:3000";
  };

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleLoad = () => {
      setIsLoaded(true);
      onLoad?.();
      onReady?.();
    };

    const handleError = () => {
      setHasError(true);
      onError?.(tUi("litTwitchEmbed.neUdalosZagruzitStrim"));
    };

    // Обработчик сообщений от Twitch Player
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== "https://player.twitch.tv") return;

      const data = event.data;
      if (!data || typeof data !== "object") return;

      switch (data.event) {
        case "player-ready":
          onReady?.();
          break;
        case "video-play":
          onPlay?.();
          break;
        case "video-pause":
          onPause?.();
          break;
        case "video-ended":
          onEnded?.();
          break;
        case "video-error":
          onError?.(tUi("litTwitchEmbed.oshibkaVosproizvedeniyaVideo"));
          break;
      }
    };

    iframe.addEventListener("load", handleLoad);
    iframe.addEventListener("error", handleError);
    window.addEventListener("message", handleMessage);

    return () => {
      iframe.removeEventListener("load", handleLoad);
      iframe.removeEventListener("error", handleError);
      window.removeEventListener("message", handleMessage);
    };
  }, [channel, onError, onLoad, onReady, onPlay, onPause, onEnded, tUi]);

  if (hasError) {
    return (
      <div className="w-full h-full bg-gray-900 flex items-center justify-center p-6">
        <div className="text-center text-white max-w-md">
          <div className="mb-6">
            <div className="w-16 h-16 mx-auto mb-4 bg-yellow-600 rounded-full flex items-center justify-center">
              <Play className="h-8 w-8" />
            </div>
            <h3 className="text-lg font-semibold mb-2">
              {tUi("twitchEmbed.vstraivanieOgranicheno")}
            </h3>
            <div className="text-sm text-gray-300 space-y-2 mb-6">
              <p>
                {tUi("twitchEmbed.twitchBlokiruetVstraivanieStrimov")}
              </p>
              <p className="text-xs text-gray-400">
                {tUi("twitchEmbed.dlyaVstraivaniyaNeobhodimoZaregistrirova")}
              </p>
            </div>
          </div>
          <div className="space-y-3">
            <Button
              onClick={() =>
                window.open(`https://www.twitch.tv/${channel}`, "_blank")
              }
              className="bg-purple-600 hover:bg-purple-700 w-full"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {tUi("twitchEmbed.smotretNaTwitch")}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setHasError(false);
                setIsLoaded(false);
              }}
              className="w-full"
            >
              {tUi("twitchEmbed.poprobovatSnova")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full relative">
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-900 flex items-center justify-center z-10">
          <div className="text-center text-white">
            <div className="animate-spin w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full mx-auto mb-2"></div>
            <p className="text-sm">{tUi("twitchEmbed.zagruzkaStrima")}</p>
          </div>
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={`https://player.twitch.tv/?channel=${channel}&parent=${getCurrentHost()}&autoplay=${autoplay}&muted=${muted}&quality=${quality}&theme=${theme}&layout=${layout}${time ? `&time=${time}` : ""}`}
        width={width}
        height={height}
        allowFullScreen={allowFullscreen}
        title={`Twitch: ${channel}`}
        className="w-full h-full"
        style={{ border: "none" }}
      />
    </div>
  );
}
